import config from "@/config";

import { loadScript } from "./load-script";
import { rememberSource } from "./provenance";
import type { Connector } from "./types";

const SDK_SRC = "https://js.live.net/v7.2/OneDrive.js";

// `folder` keeps the picker navigable.
const PICKABLE = "folder,.pdf,.docx,.xlsx,.txt,.md,.csv";

const DOWNLOAD_URL = "@microsoft.graph.downloadUrl";

export interface OneDriveItem {
    name: string;
    file?: { mimeType?: string };
    webUrl?: string;
    "@microsoft.graph.downloadUrl"?: string;
}

// The link is pre-authenticated: an Authorization header makes it 401.
export async function downloadItems(items: OneDriveItem[]): Promise<File[]> {
    const files: File[] = [];
    for (const item of items) {
        const url = item[DOWNLOAD_URL];
        if (!url) {
            throw new Error(
                `OneDrive gave no download link for "${item.name}".`
            );
        }
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(
                `OneDrive would not hand over "${item.name}" (${response.status}).`
            );
        }
        const blob = await response.blob();
        files.push(
            rememberSource(
                new File([blob], item.name, {
                    type:
                        item.file?.mimeType ||
                        blob.type ||
                        "application/octet-stream",
                }),
                { source: "onedrive", url: item.webUrl }
            )
        );
    }
    return files;
}

interface OneDriveSdk {
    open(options: {
        clientId: string;
        action: string;
        multiSelect: boolean;
        advanced: {
            redirectUri: string;
            filter: string;
            queryParameters: string;
        };
        success: (response: { value?: OneDriveItem[] }) => void;
        cancel: () => void;
        error: (error: unknown) => void;
    }): void;
}

const sdk = () => (window as unknown as { OneDrive?: OneDriveSdk }).OneDrive;

export const oneDrive: Connector = {
    id: "onedrive",
    label: "OneDrive",
    icon: "pi pi-microsoft",
    get isConfigured() {
        return Boolean(config.oneDrivePicker?.clientId);
    },
    preload: () => loadScript(SDK_SRC),
    pick: async () => {
        await loadScript(SDK_SRC);
        const oneDriveSdk = sdk();
        if (!oneDriveSdk) throw new Error("The OneDrive picker did not load.");

        const items = await new Promise<OneDriveItem[]>((resolve, reject) => {
            oneDriveSdk.open({
                clientId: config.oneDrivePicker.clientId,
                // The other actions return share links, which we cannot read.
                action: "download",
                multiSelect: true,
                advanced: {
                    redirectUri: config.oneDrivePicker.redirectUri,
                    filter: PICKABLE,
                    // Without this the response omits the download link.
                    queryParameters: `select=id,name,size,file,webUrl,${DOWNLOAD_URL}`,
                },
                success: (response) => resolve(response.value ?? []),
                cancel: () => resolve([]),
                error: (error) =>
                    reject(
                        new Error(
                            (error as { message?: string })?.message ??
                                "The OneDrive picker failed."
                        )
                    ),
            });
        });

        return items.length ? await downloadItems(items) : [];
    },
};
